import { useState } from 'react';
import { Link, useSearchParams } from 'react-router-dom';
import { Lock, Spinner, CheckCircle, Warning, Eye, EyeSlash } from '@phosphor-icons/react';
import API_URL from '../utils/apiUrl';

export default function ResetPassword() {
  const [searchParams] = useSearchParams();
  const token = searchParams.get('token');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [showPassword, setShowPassword] = useState(false);
  const [loading, setLoading] = useState(false);
  const [success, setSuccess] = useState(false);
  const [error, setError] = useState('');

  if (!token) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="neo-card max-w-md w-full text-center p-8">
          <Warning size={48} className="text-destructive mx-auto mb-4" />
          <h2 className="text-xl font-heading font-bold mb-2">Invalid Reset Link</h2>
          <p className="text-muted-foreground mb-4">This password reset link is missing or invalid.</p>
          <Link to="/forgot-password" className="neo-btn inline-block">Request a New Link</Link>
        </div>
      </div>
    );
  }

  const handleSubmit = async (e) => {
    e.preventDefault();
    setError('');

    if (password.length < 6) {
      setError('Password must be at least 6 characters.');
      return;
    }
    if (password !== confirmPassword) {
      setError('Passwords do not match.');
      return;
    }

    setLoading(true);
    try {
      const res = await fetch(`${API_URL}/api/auth/reset-password`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password }),
      });
      const data = await res.json();
      if (res.ok) {
        setSuccess(true);
      } else {
        setError(data.detail || 'Failed to reset password. The link may have expired.');
      }
    } catch {
      setError('Could not connect. Please try again.');
    }
    setLoading(false);
  };

  if (success) {
    return (
      <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
        <div className="neo-card max-w-md w-full text-center p-8">
          <div className="w-16 h-16 bg-emerald-500 rounded-full border-2 border-foreground flex items-center justify-center mx-auto mb-4 shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <CheckCircle size={32} weight="fill" className="text-white" />
          </div>
          <h2 className="text-2xl font-heading font-bold mb-2">Password Reset!</h2>
          <p className="text-muted-foreground mb-6">Your password has been updated successfully. You can now log in with your new password.</p>
          <Link to="/login" className="neo-btn-primary inline-block px-8 py-3 font-bold" data-testid="go-to-login">Log In</Link>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-[calc(100vh-4rem)] flex items-center justify-center px-4">
      <div className="neo-card max-w-md w-full p-8" data-testid="reset-password-page">
        <div className="flex items-center gap-3 mb-6">
          <div className="w-12 h-12 bg-primary rounded-xl border-2 border-foreground flex items-center justify-center shadow-[3px_3px_0px_0px_rgb(var(--9ready-shadow))]">
            <Lock size={24} weight="fill" className="text-white" />
          </div>
          <div>
            <h1 className="text-xl font-heading font-bold">Set New Password</h1>
            <p className="text-sm text-muted-foreground">Choose a strong password</p>
          </div>
        </div>

        <form onSubmit={handleSubmit} className="space-y-4">
          <div>
            <label htmlFor="password" className="block text-sm font-bold mb-1">New Password</label>
            <div className="relative">
              <input
                id="password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => setPassword(e.target.value)}
                className="neo-input w-full pr-10"
                placeholder="At least 6 characters"
                required
                minLength={6}
                data-testid="reset-password-input"
              />
              <button type="button" onClick={() => setShowPassword(!showPassword)}
                className="absolute right-3 top-1/2 -translate-y-1/2 text-muted-foreground hover:text-foreground">
                {showPassword ? <EyeSlash size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label htmlFor="confirm" className="block text-sm font-bold mb-1">Confirm Password</label>
            <input
              id="confirm"
              type={showPassword ? 'text' : 'password'}
              value={confirmPassword}
              onChange={(e) => setConfirmPassword(e.target.value)}
              className="neo-input w-full"
              placeholder="Type it again"
              required
              data-testid="reset-confirm-input"
            />
          </div>

          {error && <p className="text-destructive text-sm" data-testid="reset-error">{error}</p>}

          <button
            type="submit"
            disabled={loading}
            className="neo-btn-primary w-full !py-3 font-bold flex items-center justify-center gap-2"
            data-testid="reset-submit"
          >
            {loading ? <Spinner size={18} className="animate-spin" /> : <Lock size={18} weight="bold" />}
            {loading ? 'Resetting...' : 'Reset Password'}
          </button>
        </form>
      </div>
    </div>
  );
}
